import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ConfirmationDialog from '@/shared/ui/ConfirmationDialog';

interface SafeFormContextProps {
  showConfirmation: (confirmCallback: () => void, cancelCallback: () => void) => void;
}

const SafeFormContext = createContext<SafeFormContextProps | null>(null);

export const SafeFormProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();

  const confirmAction = useRef<() => void>();
  const cancelAction = useRef<() => void>();

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);

  const showConfirmation = useCallback(
    (confirmCallback: () => void, cancelCallback: () => void) => {
      confirmAction.current = confirmCallback;
      cancelAction.current = cancelCallback;
      setDialogOpen(true);
    },
    [],
  );

  const onConfirm = useCallback(() => {
    confirmAction.current?.();
    setDialogOpen(false);
  }, []);

  const onCancel = useCallback(() => {
    cancelAction.current?.();
    setDialogOpen(false);
  }, []);

  return (
    <SafeFormContext.Provider value={{ showConfirmation }}>
      {children}

      <ConfirmationDialog
        open={dialogOpen}
        handleConfirm={onConfirm}
        handleClose={onCancel}
        title={t('Leave page?')}
        description={t('You have unsaved changes, are you sure you want to leave?')}
        cancelLabel={t('No')}
        confirmLabel={t('Leave')}
      />
    </SafeFormContext.Provider>
  );
};

export const useSafeFormContext = () => {
  const context = useContext(SafeFormContext);
  if (!context) {
    throw new Error('useMap must be used within a SafeFormProvider');
  }
  return context;
};
